import {
  createContext, useContext, useEffect, useMemo, useState,
} from 'react';
import { Link } from 'react-router-dom';
import { api } from './api';
import { nomeDaAmostra } from './NomeDaAmostra';
import {
  Modulo, espacosDaSessao, moduloDoEndereco, moduloPorChave, recusaDaTela,
} from './modulos';
import { useSessao } from './sessao';

/**
 * O caminho até aqui — a trilha de navegação da moldura (onda 188).
 *
 * «Utilize breadcrumbs no Labmanager para o usuário se localizar mais
 * facilmente» (Humberto, 14/09/2026), com a captura do sistema da Suporte
 * Infra como exemplo: `← Amostra · OS OS-2026-001 · Amostra AM-01 ·
 * Adensamento Oedométrico`.
 *
 * ── O que a trilha responde ──────────────────────────────────────────
 *
 * O menu lateral diz em que MÓDULO a pessoa está; o título da tela diz em
 * que REGISTRO. O que nenhum dos dois diz é o meio do caminho: que a
 * ficha de adensamento aberta é da amostra 2607315-03, que é da OS
 * 2607315. Quem chegou pela fila do laboratório não passou por nenhuma
 * dessas telas, e é justamente quem mais precisa saber onde caiu — e
 * como sobe um degrau sem o botão «voltar» do navegador, que depois de
 * três cliques na fila leva para a fila, não para a amostra.
 *
 * ── De onde vêm os elos ──────────────────────────────────────────────
 *
 * Três fontes, nesta ordem, e a trilha é a soma delas:
 *
 *  1. **O registro de módulos.** O primeiro elo é o módulo que o menu
 *     acende (a mesma regra de `estaAqui`, pela `rota`/`raiz`), e um
 *     módulo atrás de uma porta (`dentroDe`) põe a porta antes de si:
 *     «Cadastros › Clientes e obras». Não há segunda lista.
 *  2. **O endereço, para a família OS → amostra → ensaio.** Esta é a
 *     espinha do sistema e a que mais telas têm: dez fichas de
 *     lançamento, a situação, a interpretação, a amostra, a programação.
 *     Em vez de pedir a cada tela que declare a própria trilha — quinze
 *     declarações que discordariam entre si em seis meses —, a moldura
 *     resolve sozinha, a partir do `:id` da URL, com uma chamada leve
 *     (`GET /ensaios/:id`, `/amostras/codigo/:codigo`, `/os/:id`) e um
 *     cache por endereço. As abas de um mesmo ensaio não pagam a chamada
 *     de novo.
 *  3. **A tela, quando só ela sabe.** Fichas fora dessa família
 *     (equipamento, recebimento, cliente, medição, treinamento, não
 *     conformidade, diário) declaram o rabo da trilha com `useCaminho`
 *     assim que têm o dado — uma linha por tela. O que a tela declara
 *     vem DEPOIS do módulo, nunca no lugar dele.
 *
 * ── O que a trilha não é ─────────────────────────────────────────────
 *
 * Não é o histórico do navegador. Um técnico que abriu a amostra pelo
 * leitor de QR, e daí o ensaio, vê a mesma trilha de quem veio pela OS —
 * a trilha diz onde o registro MORA, não por onde a pessoa passou. É o
 * que a torna previsível, e é por isso que o «← voltar» dela sobe um
 * degrau da hierarquia, e não da história.
 *
 * Com um elo só (a lista de OS, o painel) a trilha some: o título já diz
 * tudo e uma barra com uma palavra é moldura vazia.
 */
export interface Elo {
  /** O texto do elo («OS», «Amostra», «Adensamento»). Pode ser vazio
      quando o código sozinho identifica — a ficha do equipamento. */
  rotulo: string;
  /** O código ou número, em monoespaçada, como no resto do sistema. */
  codigo?: string;
  /** Para onde o elo leva; sem isto é texto, não link. */
  para?: string;
}

/** O elo por extenso, para o título da aba e o botão «voltar». */
const textoDoElo = (e: Elo): string => [e.rotulo, e.codigo].filter(Boolean).join(' ');

interface Contexto {
  declarar: (elos: Elo[] | null) => void;
}

const CaminhoContexto = createContext<Contexto>({ declarar: () => undefined });

export const ProvedorDoCaminho = CaminhoContexto.Provider;

/**
 * A tela declara o rabo da própria trilha.
 *
 * Só o que vem DEPOIS do módulo: a ficha de equipamento declara
 * `[{ rotulo: 'BAL-01', mono: true }]` e a moldura monta «Equipamentos e
 * calibrações › BAL-01». Passe `null` enquanto o dado não chegou — a
 * trilha mostra o módulo e espera. A declaração some quando a tela sai.
 */
export function useCaminho(elos: Elo[] | null): void {
  const { declarar } = useContext(CaminhoContexto);
  // A chave textual evita redeclarar a cada render por um array novo com o
  // mesmo conteúdo — que faria a trilha piscar.
  const chave = JSON.stringify(elos);
  useEffect(() => {
    declarar(elos);
    return () => declarar(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave, declarar]);
}

/** O elo de um módulo: título e, quando a rota é fixa, o link. */
function eloDoModulo(m: Modulo): Elo {
  const rota = m.rota && !m.rota.includes('/:') ? m.rota : undefined;
  return { rotulo: m.titulo, para: rota === '/' ? '/painel' : rota };
}

/** O módulo e, antes dele, a porta por onde se chega (`dentroDe`). */
function elosDoModulo(m: Modulo | undefined): Elo[] {
  if (!m) return [];
  const porta = m.dentroDe ? moduloPorChave(m.dentroDe) : undefined;
  return [...(porta ? [eloDoModulo(porta)] : []), eloDoModulo(m)];
}

// ── A família OS → amostra → ensaio, resolvida pelo endereço ────────

/** O que a moldura precisa saber de um ensaio para montar a trilha. */
interface EnsaioResumo {
  id: string;
  os_id: string | null; os_numero: string | null;
  amostra_codigo: string | null;
  amostra_identificacao?: string | null;
  metodo_nome: string | null;
  telas_lancamento: string[] | null;
}

interface AmostraResumo {
  codigo: string; os_id: string | null; os_numero: string | null;
  identificacao_recebida?: string | null;
}

interface OsResumo { id: string; numero: string | number }

/**
 * O que cada sub-tela de ensaio se chama na trilha.
 *
 * A ficha de lançamento é o próprio módulo (`/ensaio/:id/adensamento` →
 * «Adensamento»); estas são as telas que vêm DEPOIS dela.
 */
const ETAPA_DO_ENSAIO: Record<string, string> = {
  situacao: 'Situação',
  interpretacao: 'Interpretação',
};

const ELOS_FIXOS: Record<string, string> = {
  '/os/nova': 'Nova ordem de serviço',
  '/recebimento/novo': 'Novo recebimento',
  '/treinamentos/novo': 'Novo treinamento',
  '/equipamentos/pendencias': 'Pendências da metrologia',
  '/equipamentos/aferir-massas': 'Calibração interna em lote',
  '/equipamentos/verificacoes': 'Calibração interna',
};

const OS_MODULO = () => eloDoModulo(moduloPorChave('os') as Modulo);
const eloDaOs = (id: string, numero: string | number | null): Elo =>
  ({ rotulo: 'OS', codigo: numero === null ? undefined : String(numero), para: `/os/${id}` });
/*
 * Onda 303 (RN-248): a amostra aparece na trilha pelo nome que o cliente deu
 * («Amostra SP-01/2 · AM-2026-00202»), com o código logo depois — o link
 * continua pelo código, que é o do QR. A OS já está no elo anterior.
 */
const eloDaAmostra = (codigo: string, identificacao?: string | null): Elo =>
  ({ rotulo: 'Amostra', codigo: nomeDaAmostra({ codigo, identificacao }),
    para: `/amostra/${encodeURIComponent(codigo)}` });

/**
 * Cache por endereço.
 *
 * As quatro abas de um adensamento são quatro endereços do mesmo ensaio;
 * a chave do cache é o que identifica o REGISTRO (`ensaio:<id>`), não a
 * tela, para que trocar de aba não pague a chamada de novo. Vive
 * enquanto a página viver — a trilha muda quando a OS muda de número,
 * o que não acontece.
 */
const cache = new Map<string, Promise<Elo[]>>();
const cacheDeEnsaio = new Map<string, Promise<EnsaioResumo>>();

function obterEnsaio(id: string): Promise<EnsaioResumo> {
  if (!cacheDeEnsaio.has(id)) cacheDeEnsaio.set(id, api<EnsaioResumo>(`/ensaios/${id}`));
  return cacheDeEnsaio.get(id) as Promise<EnsaioResumo>;
}

/** OS › amostra — o tronco comum de toda tela de ensaio. */
function troncoDoEnsaio(e: EnsaioResumo): Elo[] {
  const elos: Elo[] = [OS_MODULO()];
  if (e.os_id) elos.push(eloDaOs(e.os_id, e.os_numero));
  if (e.amostra_codigo) elos.push(eloDaAmostra(e.amostra_codigo, e.amostra_identificacao));
  return elos;
}

async function resolverAmostra(codigo: string): Promise<Elo[]> {
  const a = await api<AmostraResumo>(`/amostras/codigo/${encodeURIComponent(codigo)}`);
  if (a.os_id) return [OS_MODULO(), eloDaOs(a.os_id, a.os_numero),
    eloDaAmostra(a.codigo, a.identificacao_recebida)];
  return [eloDoModulo(moduloPorChave('amostras') as Modulo),
    eloDaAmostra(a.codigo, a.identificacao_recebida)];
}

async function resolverOs(id: string): Promise<Elo[]> {
  const os = await api<OsResumo>(`/os/${id}`);
  return [OS_MODULO(), eloDaOs(os.id, os.numero)];
}

/**
 * O que o endereço sozinho diz.
 *
 * Devolve a parte SÍNCRONA (o módulo e os elos fixos) e, quando há um
 * registro no meio do caminho, a promessa dos elos que dependem dele. A
 * tela de ensaio é o caso mais rico: `/ensaio/<id>/adensamento/interpretacao`
 * vira «Ordens de Serviço › OS n › Amostra c › Adensamento › Interpretação»,
 * com o Adensamento apontando para a ficha.
 */
/** As abas da OS (256), com o nome que a aba mostra. */
const ABA_NA_TRILHA: Record<string, string> = {
  escopo: 'Escopo', amostras: 'Amostras', programacao: 'Programação',
  quantitativos: 'Quantitativos', historico: 'Histórico',
};

function lerEndereco(onde: string): { fixos: Elo[]; pendente?: Promise<Elo[]> } {
  const m = moduloDoEndereco(onde);

  const ensaio = onde.match(/^\/ensaio\/([^/]+)\/([^/]+)(?:\/([^/]+))?$/);
  if (ensaio) {
    const [, id, tela, etapa] = ensaio;
    const pendente = obterEnsaio(id).then((e) => {
      const base = troncoDoEnsaio(e);
      if (tela === 'situacao') {
        // «Situação» vem depois do método, e o método aponta para a
        // primeira ficha de lançamento dele.
        const primeira = e.telas_lancamento?.[0];
        const metodo: Elo[] = e.metodo_nome
          ? [{ rotulo: e.metodo_nome, para: primeira ? `/ensaio/${id}/${primeira}` : undefined }]
          : [];
        return [...base, ...metodo, { rotulo: ETAPA_DO_ENSAIO.situacao }];
      }
      const ficha: Elo = m
        ? { rotulo: m.titulo, para: etapa ? `/ensaio/${id}/${tela}` : undefined }
        : { rotulo: tela };
      const rabo: Elo[] = [ficha];
      if (etapa) rabo.push({ rotulo: ETAPA_DO_ENSAIO[etapa] ?? etapa });
      return [...base, ...rabo];
    });
    return { fixos: [], pendente };
  }

  const amostra = onde.match(/^\/amostra\/([^/]+)$/);
  if (amostra) {
    const codigo = decodeURIComponent(amostra[1]);
    const chave = `amostra:${codigo.toUpperCase()}`;
    if (!cache.has(chave)) cache.set(chave, resolverAmostra(codigo));
    return { fixos: [], pendente: cache.get(chave) };
  }

  const os = onde.match(/^\/os\/([^/]+)(?:\/(programar|programacao|campo|escopo|amostras|quantitativos|historico))?$/);
  if (os && os[1] !== 'nova') {
    const [, id, sub] = os;
    const chave = `os:${id}`;
    if (!cache.has(chave)) cache.set(chave, resolverOs(id));
    const pendente = cache.get(chave) as Promise<Elo[]>;
    /* O rabo da trilha: a programação (com o nome que a ficha usa desde a
       204) ou o campo da OS — furos e diários, que saíram da ficha. */
    const rabo = sub === 'programar'
      ? 'Programação de Amostras e Ensaios'
      : sub === 'campo' ? 'Campo'
        /* 256: as abas da OS repetem na trilha o nome da aba. */
        : sub ? ABA_NA_TRILHA[sub] ?? null : null;
    return {
      fixos: [],
      pendente: rabo
        ? pendente.then((base) => [...base, { rotulo: rabo }])
        : pendente,
    };
  }

  const fixos = elosDoModulo(m);
  if (ELOS_FIXOS[onde]) fixos.push({ rotulo: ELOS_FIXOS[onde] });
  return { fixos };
}

// ── A trilha na tela ────────────────────────────────────────────────

export function Caminho({ onde, declarados }: {
  onde: string;
  /** O que a tela corrente declarou por `useCaminho`, se declarou. */
  declarados: Elo[] | null;
}) {
  const leitura = useMemo(() => lerEndereco(onde), [onde]);
  const [resolvidos, setResolvidos] = useState<Elo[] | null>(null);
  const { eu } = useSessao();

  useEffect(() => {
    let vivo = true;
    setResolvidos(null);
    if (!leitura.pendente) return undefined;
    leitura.pendente
      .then((elos) => { if (vivo) setResolvidos(elos); })
      /* Sem permissão ou sem rede a trilha mostra só o módulo — a tela
         por baixo já explica o erro; a trilha não repete. */
      .catch(() => { if (vivo) setResolvidos(null); });
    return () => { vivo = false; };
  }, [leitura]);

  let elos: Elo[];
  if (leitura.pendente) {
    elos = resolvidos ?? elosDoModulo(moduloDoEndereco(onde));
  } else {
    elos = [...leitura.fixos, ...(declarados ?? [])];
  }
  /*
   * A trilha só é link para onde a pessoa pode ir (H1, onda 295). O Campo da
   * OS (`/os/:id/campo`) se lê «Ordens de Serviço › OS n › Campo», e as duas
   * primeiras são do Hub e do LabManager: para o sondador, que só tem o
   * Campo, seriam dois links — e o «← OS n» — para a recusa do Hub. O elo
   * fica, como texto (ele diz de que OS é a tela); o link sai. A regra é a
   * mesma da moldura (`recusaDaTela`), para os dois nunca discordarem.
   */
  const alcancados = espacosDaSessao(eu?.espacos ?? []);
  elos = elos.map((e) => (e.para && recusaDaTela(e.para.split('?')[0], null, alcancados)
    ? { ...e, para: undefined } : e));

  if (elos.length < 2) return null;

  const atual = elos[elos.length - 1];
  // «Voltar» sobe ao elo mais próximo que tenha para onde ir.
  const volta = [...elos.slice(0, -1)].reverse().find((e) => e.para);

  return (
    <nav className="caminho" aria-label="Onde você está">
      {volta && (
        <Link to={volta.para as string} className="caminho-volta"
          title={`Voltar para ${textoDoElo(volta)}`}>
          <span aria-hidden="true">←</span>
          <span className="caminho-volta-txt">{textoDoElo(volta)}</span>
        </Link>
      )}
      <ol>
        {elos.map((e, i) => {
          const ultimo = i === elos.length - 1;
          const conteudo = (
            <>
              {e.rotulo}
              {e.codigo && <>{e.rotulo ? ' ' : ''}<span className="mono">{e.codigo}</span></>}
            </>
          );
          return (
            <li key={`${i}-${textoDoElo(e)}`}>
              {i > 0 && <span className="caminho-sep" aria-hidden="true">›</span>}
              {!ultimo && e.para
                ? <Link to={e.para}>{conteudo}</Link>
                : <span aria-current={ultimo ? 'page' : undefined}>{conteudo}</span>}
            </li>
          );
        })}
      </ol>
      {/* O leitor de tela ouve a página inteira; o olho vê o último elo em
          negrito. `atual` existe para o título da aba do navegador: */}
      <TituloDaAba elos={elos} atual={atual} />
    </nav>
  );
}

/**
 * O título da aba acompanha a trilha: «Adensamento · Amostra 2607315-03 ·
 * Labmanager». Com dez abas abertas na bancada, é assim que se acha a
 * certa — e o histórico do navegador passa a dizer algo.
 */
function TituloDaAba({ elos, atual }: { elos: Elo[]; atual: Elo }) {
  const pai = elos.length > 1 ? textoDoElo(elos[elos.length - 2]) : null;
  const titulo = [textoDoElo(atual), pai, 'Labmanager'].filter(Boolean).join(' · ');
  useEffect(() => {
    const anterior = document.title;
    document.title = titulo;
    return () => { document.title = anterior; };
  }, [titulo]);
  return null;
}
